/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import UnifiedDayHeader from './UnifiedDayHeader';
import MonthDayContent from './MonthDayContent';
import Text from '../../../../shared/components/Text/Text';
import abbreviateStatNumber from '../../../../shared/utils/abbreviateStatNumber';
import useAbbreviateOnOverflow from '../../hooks/useAbbreviateOnOverflow';

const DayCell = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-width: 220px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 4px;
  min-height: 200px;
  overflow: hidden;
  box-sizing: border-box;
  /** Active day: a 2px border (always present, transparent when inactive,
   * so there's no layout shift on toggle). A border lives inside the
   * cell's own box, so — unlike an outer box-shadow — it wraps the whole
   * cell without the header's white background covering the top, and the
   * scroll viewport never clips it at the right edge. The 4px padding adds
   * breathing room inside the border when active. */
  border: 2px solid ${(props) => (props.isActiveSelectionDay ? props.theme.colors.hover : 'transparent')};
  padding: ${(props) => (props.isActiveSelectionDay ? '4px' : '0')};
  /** Month view alternates a subtle shade between adjacent calendar months so
   * boundaries stay visible in the month-decoupled rolling window. Opacity (not
   * background) so the shade covers the whole cell uniformly — the header and
   * stats bar paint their own white backgrounds. */
  opacity: ${(props) => {
    if (props.isPaddingDay) return 0.5;
    if (props.isShadedMonth) return 0.72;
    return 1;
  }};
  transition: opacity 0.2s ease, border-color 0.15s ease, background 0.15s ease;

  &:hover {
    opacity: ${(props) => (props.isPaddingDay ? 0.7 : 1)};
  }
`;

const StatsBar = styled('div')`
  position: relative;
  background: ${({ theme }) => theme.colors.surface};
  min-height: 40px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
  margin-top: auto;
`;

const StatsBarContent = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;
`;

const StatsBarItem = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

/**
 * Month-view day cell. Owns:
 *  - the rbd `Droppable` for `month-day-{date}`
 *  - a sticky `isInteractive` flag that flips the cell's items from
 *    plain `ReadOnlyExerciseItem` to `DraggableMonthExerciseItem` once
 *    the user hovers/focuses the cell. This avoids mounting hundreds
 *    of `Draggable` nodes across a dense month grid up front.
 */
const MonthDayDroppableCell = ({
  date,
  dayData,
  isPaddingDay,
  isShadedMonth,
  isMonthItemsLoading,
  programType,
  isAnalyticsDisabled,
}) => {
  const [isInteractive, setIsInteractive] = useState(false);
  const isDragInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );
  const { colors } = useTheme();
  const dateKey = date.format('YYYY-MM-DD');
  /** The day that currently owns the month multi-selection (null when
   * nothing is selected). Drives this cell's highlight. */
  const isActiveSelectionDay = useSelector(
    (state) => state.calendar.data.monthSelectionActiveDate === dateKey,
  );

  /** Flip on first interaction; sticky for the lifetime of the cell. */
  const activate = useCallback(() => {
    if (!isInteractive) setIsInteractive(true);
  }, [isInteractive]);

  /** Activate during an in-flight drag too, so a cell can become a
   * destination even if the user hasn't hovered it before grabbing. */
  const shouldRenderDraggables = isInteractive || isDragInProgress;

  const droppableId = `month-day-${dateKey}`;

  /** Compact S/R/V bar: show full numbers, abbreviating only when the full row overflows the cell
   * (measured off the hidden static copy below). */
  const sets = dayData?.setCount || 0;
  const reps = dayData?.repCount || 0;
  const vol = dayData?.totalVolumeLoad || 0;
  const { containerRef, measureRef, abbreviate } = useAbbreviateOnOverflow(`${sets}-${reps}-${vol}`);
  const fmtStat = (n) => (abbreviate ? abbreviateStatNumber(n) : n.toLocaleString('en-US'));

  return (
    <DayCell
      isPaddingDay={isPaddingDay}
      isShadedMonth={isShadedMonth}
      isActiveSelectionDay={isActiveSelectionDay}
      data-month-day-cell
      data-date={dateKey}
      onMouseEnter={activate}
      onFocusCapture={activate}
    >
      <UnifiedDayHeader
        programType={programType}
        viewType='monthView'
        date={date}
        isExpanded={false}
        onToggleExpand={null}
        showWorkoutName
        isWeekView={false}
      />
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <MonthDayContent
            date={date}
            exercises={dayData?.exercises || []}
            isLoading={isMonthItemsLoading}
            droppableProvided={provided}
            isInteractive={shouldRenderDraggables}
          />
        )}
      </Droppable>
      {!isAnalyticsDisabled && (
        <StatsBar>
          <StatsBarContent ref={containerRef}>
            <StatsBarItem>
              <Text fontSize='13px' color={colors.textSecondary}>S</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color={colors.text} fontWeight='700'>
                {fmtStat(sets)}
              </Text>
            </StatsBarItem>
            <StatsBarItem>
              <Text fontSize='13px' color={colors.textSecondary}>R</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color={colors.text} fontWeight='700'>
                {fmtStat(reps)}
              </Text>
            </StatsBarItem>
            <StatsBarItem>
              <Text fontSize='13px' color={colors.textSecondary}>V</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color={colors.text} fontWeight='700'>
                {fmtStat(vol)}
              </Text>
            </StatsBarItem>
          </StatsBarContent>
          {/* Hidden static full-value copy used only to measure whether the full row overflows. */}
          <StatsBarContent
            ref={measureRef}
            aria-hidden
            style={{
              width: 'auto',
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            <StatsBarItem>
              <Text fontSize='13px' color={colors.textSecondary}>S</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color={colors.text} fontWeight='700'>
                {sets.toLocaleString('en-US')}
              </Text>
            </StatsBarItem>
            <StatsBarItem>
              <Text fontSize='13px' color={colors.textSecondary}>R</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color={colors.text} fontWeight='700'>
                {reps.toLocaleString('en-US')}
              </Text>
            </StatsBarItem>
            <StatsBarItem>
              <Text fontSize='13px' color={colors.textSecondary}>V</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color={colors.text} fontWeight='700'>
                {vol.toLocaleString('en-US')}
              </Text>
            </StatsBarItem>
          </StatsBarContent>
        </StatsBar>
      )}
    </DayCell>
  );
};

export default React.memo(MonthDayDroppableCell);
